/** SuggestPromptDialog — preview a Claude-suggested prompt before saving a draft.
 *
 * Shows the current prompt beside the proposed one so the owner sees what
 * changed; "Save as draft" persists the proposal (does not activate it).
 */

import { Loader2 } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import type { SuggestPromptResult } from '@/entities/eval';

export default function SuggestPromptDialog({
  open,
  suggestion,
  isSuggesting,
  isSaving,
  onSave,
  onClose,
}: {
  open: boolean;
  suggestion: SuggestPromptResult | null;
  isSuggesting: boolean;
  isSaving: boolean;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Suggested prompt improvement</DialogTitle>
          <DialogDescription>
            {suggestion?.change_summary || 'Review the proposed prompt before saving it as a draft.'}
          </DialogDescription>
        </DialogHeader>

        {suggestion && (
          <div className="grid max-h-[50vh] grid-cols-2 gap-3 overflow-auto">
            <div className="flex flex-col gap-1">
              <h4 className="text-xs font-semibold text-muted-foreground">Current</h4>
              <pre className="whitespace-pre-wrap rounded border border-border bg-muted p-2 text-xs text-foreground">
                {suggestion.current_content}
              </pre>
            </div>
            <div className="flex flex-col gap-1">
              <h4 className="text-xs font-semibold text-status-success">Proposed</h4>
              <pre className="whitespace-pre-wrap rounded border border-border bg-card p-2 text-xs text-foreground">
                {suggestion.proposed_content}
              </pre>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSuggesting || isSaving || !suggestion}>
            {isSuggesting && <Loader2 data-icon="inline-start" className="animate-spin" aria-hidden="true" />}
            {isSuggesting ? 'Generating suggestion…' : isSaving ? 'Saving…' : 'Save as draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
